import { Search, Settings } from '.generated/Feature.CW.model';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import Pagination from 'helpers/Coveo/Pagination';
import QuerySummary from 'helpers/Coveo/QuerySummary';
import Sort from 'helpers/Coveo/SortDropdown';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import {
  CoveoEngineProvider,
  useCoveoEngine,
} from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { useSearch } from 'lib/utils/coveo/SearchContext/SearchContext';
import { useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import { buildInteractiveResult, InteractiveResult, Result } from '@coveo/headless';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import AppliedFacetFilters from 'helpers/Coveo/AppliedFacetFilters';
import UrlManagerController from 'helpers/Coveo/UrlManagerController';
import { ButtonGroupProps } from 'helpers/SitecoreWrappers/ButtonGroup/ButtonGroup';
import InitialsAndFacets from 'helpers/Coveo/InitialsAndFacets';
import { SearchBar } from './GlobalSearch';
import type { LocationInputHandle } from './GlobalSearch';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import classNames from 'classnames';
import { SvgIcon } from 'helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import NoResultsMessage from 'helpers/Coveo/NoResultsMessage';

export type AZTeachingSheetsProps = Search.AZSearchTeachingSheets &
  ButtonGroupProps & {
    params: {
      Theme: string;
    };
    templateId: string;
  };

export type PageSettings = Settings.SiteSettings['fields'];

type TeachingSheetRaw = {
  cwformat?: string[];
  cwtitle?: string;
  cwdescription?: string;
};

const AZTeachingSheets = (props: AZTeachingSheetsProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;
  //const shouldRedirect = false;

  const [coveoConfig, setCoveoConfig] = useState({
    searchHub: '',
    pipeline: '',
    accessToken: '',
    organizationId: '',
  });

  useEffect(() => {
    setCoveoConfig({
      searchHub: contextWithSettings?.aZSearchHubTeachingSheets?.value || '',
      pipeline: contextWithSettings?.aZPipelineTeachingSheets?.value || '',
      accessToken: contextWithSettings?.aZAccessTokenTeachingSheets?.value || '',
      organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
    });
  }, [contextWithSettings]);

  if (!coveoConfig?.organizationId && !coveoConfig?.accessToken && !coveoConfig?.pipeline) {
    return <></>;
  }

  return (
    <>
      <CoveoEngineProvider config={coveoConfig}>
        <AZTeachingSheetsResultList {...props} />
        <UrlManagerController />
      </CoveoEngineProvider>
    </>
  );
};

export default AZTeachingSheets;

export const AZTeachingSheetsResultList = (props: AZTeachingSheetsProps): JSX.Element => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { getDictionaryValue } = useDictionary();
  const engine = useCoveoEngine(); //Custom hook for engine , engine connects NextApp to Coveo Search
  const [firstSearchExecuted, setFirstSearchExecuted] = useState<boolean>(false);

  const fieldsToInclude = [
    'cwdescription',
    'cwinitial',
    'cwpagetype',
    'cwtitle',
    'clickUri',
    'cwformat',
    'cwdraftnumber',
  ];
  const { ContextResults, setContextResults } = useSearch();
  // Inside component
  const searchBarRef = useRef<LocationInputHandle>(null);

  //   const handleKeywordClick = (keyword: string) => {
  //     searchBarRef.current?.setValue(keyword);
  //   };

  //const searchKeywords = props?.fields?.searchKeywords;

  const TAILWIND_VARIANTS = tv({
    slots: {
      base: ['bg-blue-50 py-[64px]'],
      headingStyle: ['leading-58 font-bold text-gray-900 font-centuryGothic text-48 lg:mb-0 mb-8'],
      titleLinkWrapperStyle: ['lg:flex justify-between items-center'],
      containerStyle: ['max-w-screen-xxl mx-auto lg:px-[80px] px-10'],
      subHeadingStyle: [
        'leading-[22px] font-bold text-gray-900 font-centuryGothic text-16 my-[15px]',
      ],
    },
  });

  useEffect(() => {
    if (!engine) return;

    const unsubscribe = engine.subscribe(() => {
      setContextResults(engine.state.search);

      if (engine.state.search.results.length > 0) {
        setFirstSearchExecuted(true);
      }
    });

    // Trigger initial search after all controllers (e.g., facets) are mounted
    setTimeout(() => {
      engine.executeFirstSearch();
    }, 0);

    return () => unsubscribe();
  }, [engine]);

  // stop scroll on drawer open in mobile
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  const resultsToRender = ContextResults?.results || [];

  return (
    <>
      <ComponentWrapper className={TAILWIND_VARIANTS().base()}>
        <div className={TAILWIND_VARIANTS().containerStyle()}>
          <div className={TAILWIND_VARIANTS().titleLinkWrapperStyle()}>
            <h1 className={TAILWIND_VARIANTS().headingStyle()}>
              <PlainTextWrapper field={props?.fields?.pageTitle} />
            </h1>
          </div>
          <div>
            <SearchBar
              ref={searchBarRef}
              engine={engine}
              placeholder={props?.fields?.searchTermPlaceholderText}
              className="my-[40px]"
              autoSubmitOnSetValue={true}
            />
          </div>
          {/* <p className={TAILWIND_VARIANTS().subHeadingStyle()}>
            <PlainTextWrapper field={props?.fields?.commonSearchesLabel} />
          </p> */}
          {/* {searchKeywords?.value && (
            <div className="flex flex-wrap gap-16 mt-[15px]">
              {searchKeywords.value
                .split(',')
                .map((kw: string) => kw.trim())
                .filter((kw: string) => Boolean(kw))
                .map((kw: string) => (
                  <ButtonWrapper
                    key={kw}
                    text={kw}
                    ctaVariant="tertiary"
                    ctaIcon={undefined}
                    className="inline"
                    onClick={() => handleKeywordClick(kw)}
                  />
                ))}
            </div>
          )} */}
        </div>
      </ComponentWrapper>
      <ComponentWrapper className="bg-white">
        <div className="max-w-screen-xxl mx-auto lg:px-[80px] px-10 coveo-search-list">
          <div className="flex lg:flex-row flex-col gap-16">
            <div className="md:max-w-[248px] w-full md:mr-16 mr-0 lg:block hidden">
              <InitialsAndFacets
                initialFacetField="cwinitial"
                initialFacetLabel="First Letter"
                facetGroupProps={{
                  fields: ['cwspecialties', 'cwcontenttype', 'cwpopulation', 'cwformat'],
                  labels: {
                    cwspecialties: 'Specialty',
                    cwcontenttype: 'Content Type',
                    cwpopulation: 'Population',
                    cwformat: 'Format',
                  },
                  numberOfValues: 10,
                  sortCriteria: 'occurrences',
                }}
              />
            </div>
            {isDrawerOpen && (
              <div
                className={classNames(
                  'fixed inset-0 z-50 bg-white transform transition-transform duration-500 ease-in-out flex flex-col lg:hidden',
                  {
                    'translate-x-0': isDrawerOpen,
                    'translate-x-full': !isDrawerOpen,
                  }
                )}
              >
                <div className="flex justify-between items-center px-10 py-[25px] border-b border-gray-200">
                  <div className="text-20 font-bold leading-[28px] font-centuryGothic">
                    {getDictionaryValue('SetFilters')}
                  </div>
                  <button onClick={() => setIsDrawerOpen(false)} aria-label="Close">
                    <SvgIcon icon="close" size="lg" className="w-10 h-10 [&>path]:fill-blue-base" />
                  </button>
                </div>

                <div className="mx-10 py-[25px] flex items-center justify-between border-b-2 border-gray-50">
                  <div className="text-[30px] leading-[36px] font-bold text-black font-centuryGothic">
                    {getDictionaryValue('Filters') || 'Filters'}
                  </div>
                </div>
                <div className="flex-grow overflow-y-auto px-10">
                  <InitialsAndFacets
                    initialFacetField="cwinitial"
                    initialFacetLabel="First Letter"
                    facetGroupProps={{
                      fields: ['cwspecialties', 'cwcontenttype', 'cwpopulation', 'cwformat'],
                      labels: {
                        cwspecialties: 'Specialty',
                        cwcontenttype: 'Content Type',
                        cwpopulation: 'Population',
                        cwformat: 'Format',
                      },
                      numberOfValues: 10,
                      sortCriteria: 'occurrences',
                    }}
                  />
                </div>
                <div className="p-10 border-t border-gray-200">
                  <ButtonWrapper
                    ctaVariant="primary"
                    onClick={() => setIsDrawerOpen(false)}
                    className="w-full bg-blue-base text-white py-4 rounded-md text-18 font-semibold"
                  >
                    {getDictionaryValue('ViewResults')}
                  </ButtonWrapper>
                </div>
              </div>
            )}
            <div className="lg:hidden mb-4">
              <ButtonWrapper
                ctaVariant="secondary"
                text="Set Filters"
                ctaIcon="funnel"
                onClick={() => setIsDrawerOpen(true)}
                className="w-full [&>svg]:mt-[-5px] gap-x-4"
                ctaIconAlignment="left"
              />
            </div>

            <div className="md:w-[calc(100%-248px)]">
              <div className="py-6">
                <AppliedFacetFilters
                  fields={['cwinitial', 'cwspecialties']}
                  labels={{
                    cwinitial: 'First letter',
                    cwspecialties: 'Type',
                  }}
                />
              </div>
              <div className="flex justify-start items-center py-6 w-full flex-wrap">
                {/* Total Count of results */}
                <QuerySummary />
                {/* Sort control lets you select criteria to apply to search results : hidden as per design */}
                <div className="hidden">
                  <Sort fieldsToInclude={fieldsToInclude} enableNewestSort={true} />
                </div>
              </div>
              <div className="resultlist-wrapper">
                {firstSearchExecuted ? (
                  resultsToRender?.length > 0 ? (
                    <div className="">
                      <div>
                        <section>
                          {resultsToRender.map((item: Result, index) => {
                            const raw = item.raw as TeachingSheetRaw;
                            const controller: InteractiveResult | null = engine
                              ? buildInteractiveResult(engine, {
                                  options: { result: item },
                                })
                              : null;

                            return (
                              <article
                                role="region"
                                aria-labelledby={`${item?.raw?.cwtitle}`}
                                key={index}
                                className="lg:flex-row flex-col-reverse flex py-[30px] border-b border-b-gray-50 last:border-0 lg:gap-[46px] gap-[24px] justify-between"
                              >
                                {/* content section */}
                                <div className="w-full">
                                  <div className="flex w-full justify-between">
                                    <PlainTextWrapper
                                      field={{ value: raw?.cwformat?.[0] || '' }}
                                      tag="p"
                                      className="text-14 leading-22 font-centuryGothic font-bold text-gray-500"
                                    />
                                  </div>
                                  <a
                                    aria-label={`${item?.raw?.cwtitle}`}
                                    href={item?.clickUri}
                                    onClick={() => controller?.select()}
                                    onMouseDown={() => controller?.select()}
                                  >
                                    <h2 className="text-blue-base text-28 leading-[34px] font-bold font-centuryGothic pb-2 my-6 hover:underline hover:text-darkblue-base cursor-pointer lg:line-clamp-1 line-clamp-2">
                                      <PlainTextWrapper field={{ value: raw?.cwtitle || '' }} />
                                    </h2>
                                  </a>
                                  <div className="text-gray-900 font-nunitoSans text-16 leading-25 font-[500] ">
                                    <RichTextWrapper
                                      field={{
                                        value:
                                          raw?.cwdescription && raw.cwdescription.length > 200
                                            ? `${raw.cwdescription.slice(0, 200)}...`
                                            : raw?.cwdescription || '',
                                      }}
                                      className=""
                                    />
                                  </div>
                                </div>
                              </article>
                            );
                          })}
                        </section>
                      </div>
                    </div>
                  ) : (
                    <>
                      <NoResultsMessage />
                    </>
                  )
                ) : null}
              </div>

              <div className="pagination-wrapper">
                <Pagination fieldsToInclude={fieldsToInclude} className="justify-end mt-12" />
              </div>
            </div>
          </div>
        </div>
      </ComponentWrapper>
    </>
  );
};
